import {
  Button,
  Input,
  InputProps,
  Label,
  Link,
  makeStyles,
  useId,
} from "@fluentui/react-components";
import {
  DismissRegular,
  SendRegular,
} from "@fluentui/react-icons";
import React, { KeyboardEvent, useState } from "react";

interface ITaskLinksFieldProps {
  links: string[];
  onChange: (links: string[]) => void;
  label?: string;
}

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    rowGap: "0.5rem",
  },
  inputRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "0.5rem",
  },
  input: {
    flexGrow: 1,
  },
  list: {
    listStyleType: "none",
    marginBlock: "0",
    paddingInline: "0",
    display: "flex",
    flexDirection: "column",
    rowGap: "0.25rem",
  },
  listItem: {
    listStyleType: "none",
  },
  listRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "0.5rem",
    justifyContent: "space-between",
    paddingLeft: "0.5rem",
  },
  link: {
    flexGrow: 1,
    minWidth: 0,
    overflowWrap: "break-word",
  },
  removeButton: {
    flexShrink: 0,
  },
});

/**
 * Splits pasted text into individual links. Users often paste several URLs at
 * once separated by whitespace/newlines, so we normalize them into entries.
 */
function splitPastedLinks(raw: string): string[] {
  return raw
    .split(/[\s]+/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

const TaskLinksField: React.FC<ITaskLinksFieldProps> = ({
  links,
  onChange,
  label = "Linki do zadań",
}) => {
  const styles = useStyles();
  const inputId = useId("task-link");
  const [draft, setDraft] = useState("");

  const appendLinks = (candidates: string[]) => {
    const trimmed = candidates
      .map((candidate) => candidate.trim())
      .filter((candidate) => candidate.length > 0);

    if (trimmed.length === 0) {
      return;
    }

    const merged = [...links];
    for (const candidate of trimmed) {
      if (!merged.includes(candidate)) {
        merged.push(candidate);
      }
    }

    onChange(merged);
    setDraft("");
  };

  const addDraft = () => {
    appendLinks([draft]);
  };

  const removeLink = (index: number) => {
    onChange(links.filter((_, i) => i !== index));
  };

  // Safari does not reliably reflect pasted text through the controlled value,
  // so we read it explicitly from the clipboard and add it right away.
  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData("text");
    const parts = splitPastedLinks(pasted);

    if (parts.length === 0) {
      return;
    }

    event.preventDefault();
    appendLinks(parts);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      addDraft();
    }
  };

  const handleChange: InputProps["onChange"] = (_, data) => {
    setDraft(data.value);
  };

  return (
    <div className={styles.container}>
      <Label htmlFor={inputId}>{label}</Label>
      <div className={styles.inputRow}>
        <Input
          id={inputId}
          type="text"
          className={styles.input}
          placeholder="Wklej lub wpisz link i naciśnij Enter"
          value={draft}
          onChange={handleChange}
          onPaste={handlePaste}
          onKeyDown={handleKeyDown}
        />
        <Button
          type="button"
          appearance="primary"
          aria-label="Dodaj link"
          icon={<SendRegular />}
          disabled={draft.trim().length === 0}
          onClick={addDraft}
        />
      </div>

      {links.length > 0 && (
        <ul className={styles.list}>
          {links.map((link, index) => (
            <li className={styles.listItem} key={`${link}-${index}`}>
              <div className={styles.listRow}>
                <Link
                  className={styles.link}
                  appearance="subtle"
                  target="_blank"
                  href={link}
                >
                  {link}
                </Link>
                <Button
                  type="button"
                  appearance="subtle"
                  size="small"
                  className={styles.removeButton}
                  aria-label={`Usuń link ${link}`}
                  icon={<DismissRegular />}
                  onClick={() => removeLink(index)}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default TaskLinksField;
